import { useEffect, useState } from "react";

import type { ReviewCreate, TakeoffConfiguration } from "@/api/client";
import { Button } from "@/ui/Button";
import { cn } from "@/ui/cn";
import { Dialog } from "@/ui/Dialog";

type Scope = "all" | "one" | "block";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configuration: TakeoffConfiguration;
  configurations: readonly TakeoffConfiguration[];
  /** The panel the estimator picked. Without one, only the whole configuration can change. */
  instanceId?: string | null;
  /** Each resolves true when saved; the dialog then closes. */
  onRenameAll: (unitId: string, mark: string) => Promise<boolean>;
  onMerge: (unitId: string, intoUnitId: string) => Promise<boolean>;
  onDecide: (instanceId: string, body: ReviewCreate) => Promise<boolean>;
  /** Panels boxed together on the sheet (a block, Phase 7 K4); offered, and chosen, first. */
  blockIds?: readonly string[] | null;
  onDecideMany?: (instanceIds: readonly string[], body: ReviewCreate) => Promise<boolean>;
  error?: string | null;
  /** Which choice starts selected when a panel is given; one panel unless said otherwise. */
  defaultScope?: Scope;
}

const FIELD =
  "h-9 w-full rounded-md border border-line-strong bg-surface px-2.5 font-mono text-sm text-ink placeholder:text-muted focus:border-accent focus:outline-none";

/**
 * A new label for a configuration, or for one panel of it. Renaming every panel renames
 * the configuration, so the take-off, the markup and the download all change; one panel
 * gets a relabel decision and the rest keep their label. A label that already exists
 * means the panels join that configuration instead.
 */
export function RenameDialog({
  open,
  onOpenChange,
  configuration,
  configurations,
  instanceId = null,
  onRenameAll,
  onMerge,
  onDecide,
  blockIds = null,
  onDecideMany,
  error = null,
  defaultScope = "one",
}: Props) {
  const block = blockIds && blockIds.length > 1 && onDecideMany ? blockIds : null;
  const initialScope: Scope = block ? "block" : instanceId ? defaultScope : "all";
  const [mark, setMark] = useState(configuration.mark);
  const [scope, setScope] = useState<Scope>(initialScope);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMark(configuration.mark);
      setScope(initialScope);
    }
  }, [open, configuration.mark, initialScope]);

  const wanted = mark.trim();
  const same = wanted.toUpperCase() === configuration.mark.toUpperCase();
  const existing = configurations.find(
    (c) => c.unit_id !== configuration.unit_id && c.mark.toUpperCase() === wanted.toUpperCase(),
  );
  const panels = `${configuration.count.toLocaleString("en-US")} panel${configuration.count === 1 ? "" : "s"}`;

  const these = block ? `${block.length.toLocaleString("en-US")} panels` : "";
  let action: string;
  if (scope === "all") {
    action = existing ? `Merge into ${existing.mark}` : `Rename ${panels}`;
  } else if (scope === "block") {
    action = existing ? `Move ${these} to ${existing.mark}` : `Relabel ${these}`;
  } else {
    action = existing ? `Move panel to ${existing.mark}` : "Relabel this panel";
  }

  const submit = async () => {
    if (!wanted || same || busy) {
      return;
    }
    setBusy(true);
    const relabel: ReviewCreate = existing
      ? { decision: "relabel", corrected_unit_id: existing.unit_id }
      : { decision: "relabel", corrected_mark: wanted };
    let ok: boolean;
    if (scope === "all") {
      ok = existing
        ? await onMerge(configuration.unit_id, existing.unit_id)
        : await onRenameAll(configuration.unit_id, wanted);
    } else if (scope === "block" && block && onDecideMany) {
      ok = await onDecideMany(block, relabel);
    } else {
      ok = await onDecide(instanceId ?? "", relabel);
    }
    setBusy(false);
    if (ok) {
      onOpenChange(false);
    }
  };

  const option = (key: Scope, title: string, detail: string) => (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2.5 rounded-lg border px-3 py-2.5",
        scope === key ? "border-accent bg-accent-soft" : "border-line hover:bg-sunken",
      )}
    >
      <input
        type="radio"
        name="rename-scope"
        className="mt-1 accent-[var(--color-accent)]"
        checked={scope === key}
        onChange={() => setScope(key)}
        aria-labelledby={`rename-scope-${key}`}
        aria-describedby={`rename-scope-${key}-detail`}
      />
      <span className="flex flex-col">
        <span id={`rename-scope-${key}`} className="text-sm font-medium text-ink">
          {title}
        </span>
        <span id={`rename-scope-${key}-detail`} className="text-[13px] text-muted">
          {detail}
        </span>
      </span>
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange} label={`Rename ${configuration.mark}`}>
      <form
        className="flex flex-col gap-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <label className="flex flex-col gap-1 text-[13px] font-medium text-ink-2">
          <span>New label</span>
          <input
            type="text"
            className={FIELD}
            value={mark}
            autoFocus
            list="rename-existing-labels"
            onFocus={(event) => event.target.select()}
            onChange={(event) => setMark(event.target.value)}
          />
          <datalist id="rename-existing-labels">
            {configurations
              .filter((c) => c.unit_id !== configuration.unit_id)
              .map((c) => (
                <option key={c.unit_id} value={c.mark} />
              ))}
          </datalist>
        </label>

        {instanceId ? (
          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-[13px] font-medium text-ink-2">Apply to</legend>
            {block
              ? option(
                  "block",
                  `These ${these}`,
                  "The block you boxed on the sheet. Each panel records its own decision.",
                )
              : null}
            {option("one", "Only this panel", "Every other panel keeps its label.")}
            {option(
              "all",
              `Every panel labelled ${configuration.mark}`,
              `${panels} in this take-off.`,
            )}
          </fieldset>
        ) : null}

        {existing && !same ? (
          <p className="rounded-md bg-warn-soft px-3 py-2 text-[13px] text-ink-2">
            {existing.mark} already exists ({existing.count.toLocaleString("en-US")} panels).{" "}
            {scope === "all"
              ? `Every panel of ${configuration.mark} joins it, and ${configuration.mark} is kept as an alias.`
              : scope === "block"
                ? `These ${these} move to it.`
                : "This panel moves to it."}
          </p>
        ) : null}

        {error ? (
          <p className="text-[13px] text-danger" role="alert">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!wanted || same || busy}>
            {action}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
